import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  Users,
  ShoppingBag,
  Settings,
  LogOut,
  Plus,
  Mail,
  GitBranch,
  Inbox,
  Megaphone,
  Wrench,
  Camera,
  Globe,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface NavItem {
  title: string;
  url: string;
  icon: LucideIcon;
}

export const ADMIN_NAV: { label: string; items: NavItem[] }[] = [
  {
    label: 'Panoramica',
    items: [{ title: 'Dashboard', url: '/admin', icon: LayoutDashboard }],
  },
  {
    label: 'Operazioni',
    items: [
      { title: 'Eventi', url: '/admin/events', icon: Calendar },
      { title: 'Ordini', url: '/admin/orders', icon: ShoppingBag },
      { title: 'Clienti', url: '/admin/customers', icon: Users },
      { title: 'Fotografi', url: '/admin/photographers', icon: Camera },
      { title: 'Marketing', url: '/admin/marketing', icon: Megaphone },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { title: 'Email Templates', url: '/admin/email-templates', icon: Mail },
      { title: 'Coda Email', url: '/admin/email-queue', icon: Inbox },
      { title: 'Strumenti', url: '/admin/tools', icon: Wrench },
      { title: 'Versioni', url: '/admin/versions', icon: GitBranch },
      { title: 'Impostazioni', url: '/admin/settings', icon: Settings },
    ],
  },
];

/** Route → section title, used by the admin header. */
export function getAdminSectionTitle(pathname: string): string {
  if (pathname === '/admin') return 'Dashboard';
  if (pathname.startsWith('/admin/create-event')) return 'Nuovo Evento';
  if (pathname.startsWith('/admin/event/')) return 'Gestione Evento';
  if (pathname.startsWith('/admin/race/')) return 'Gestione Gara';
  const flat = ADMIN_NAV.flatMap((g) => g.items);
  const match = flat
    .filter((i) => i.url !== '/admin')
    .find((i) => pathname.startsWith(i.url));
  return match?.title ?? 'Amministrazione';
}

interface AdminSidebarProps {
  /** Called after navigating — used to close the mobile sheet. */
  onItemClick?: () => void;
}

export function AdminSidebar({ onItemClick }: AdminSidebarProps = {}) {
  const { state, isMobile } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const collapsed = state === 'collapsed' && !isMobile;

  const isActive = (path: string) =>
    path === '/admin' ? location.pathname === '/admin' : location.pathname.startsWith(path);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast({
      title: 'Disconnesso',
      description: 'Sei stato disconnesso con successo',
    });
    navigate('/');
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="gap-3 p-3">
        <div className={cn('flex items-center gap-2 px-1', collapsed && 'justify-center px-0')}>
          <img
            src="/lovable-uploads/61dab0b5-7f0c-46f5-9cb9-feeb93237e94.png"
            alt="Equiphoto.ch"
            className="h-6 w-auto"
          />
          {!collapsed && (
            <span className="font-serif text-sm font-semibold tracking-tight">Admin</span>
          )}
        </div>

        <Button
          onClick={() => {
            navigate('/admin/create-event');
            onItemClick?.();
          }}
          className={cn(
            'h-10 w-full justify-start gap-2 rounded-xl transition-[colors,transform] duration-200 active:scale-[0.96]',
            collapsed && 'justify-center px-0',
          )}
          title="Nuovo Evento"
        >
          <Plus className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Nuovo Evento</span>}
        </Button>
      </SidebarHeader>

      <SidebarContent className="px-1">
        {ADMIN_NAV.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel className={collapsed ? 'sr-only' : undefined}>
              {group.label}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.url)}
                      tooltip={item.title}
                      className="h-10 rounded-xl transition-[colors,transform] duration-200 active:scale-[0.98]"
                    >
                      <NavLink to={item.url} onClick={onItemClick}>
                        <item.icon className="h-4 w-4 shrink-0" />
                        <span>{item.title}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip="Visualizza Sito"
              className="h-10 rounded-xl transition-[colors,transform] duration-200 active:scale-[0.98]"
            >
              <a href="/" target="_blank" rel="noreferrer">
                <Globe className="h-4 w-4 shrink-0" />
                <span>Visualizza Sito</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleLogout}
              tooltip="Esci"
              className="h-10 rounded-xl text-destructive transition-[colors,transform] duration-200 hover:text-destructive active:scale-[0.98]"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              <span>Esci</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
